import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease-out;
  backdrop-filter: blur(5px);
`;

export const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.4s ease-out;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #1e88e5;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #1976d2;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #1e88e5 0%, #1976d2 100%);
  color: white;
  border-radius: 20px 20px 0 0;
`;

export const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  color: white;
`;

export const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

export const ModalForm = styled.form`
  padding: 2rem;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  &:has(.FormGroup:nth-child(2)) {
    grid-template-columns: 1fr 1fr;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Label = styled.label`
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

export const Input = styled.input`
  padding: 0.875rem 1rem;
  border: 2px solid ${props => props.hasError ? '#e53e3e' : '#e2e8f0'};
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#e53e3e' : '#1e88e5'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(229, 62, 62, 0.1)' : 'rgba(30, 136, 229, 0.1)'};
  }
  
  &::placeholder {
    color: #a0aec0;
  }
  
  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
  }
`;

export const ErrorMessage = styled.span`
  color: #e53e3e;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-weight: 500;
`;

export const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #1e88e5 0%, #1976d2 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(30, 136, 229, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const LoginLink = styled.p`
  text-align: center;
  margin-top: 1.5rem;
  color: #4a5568;
  font-size: 0.9rem;
  
  a {
    color: #1e88e5;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const SuccessMessage = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  
  h3 {
    color: #2d3748;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #4a5568;
    font-size: 1rem;
  }
`;

export const SuccessIcon = styled.div`
  color: #38a169;
  margin-bottom: 1rem;
  animation: ${bounce} 1s ease-in-out;

  svg {
    filter: drop-shadow(0 4px 8px rgba(56, 161, 105, 0.3));
  }
`;

export const PasswordRequirements = styled.div`
  margin-top: 0.75rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

export const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: ${props => props.isValid ? '#38a169' : '#e53e3e'};
  transition: color 0.3s ease;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const RequirementIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  background: ${props => props.isValid ? '#38a169' : '#e53e3e'};
  color: white;
  transition: all 0.3s ease;
`;

export const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

export const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #1e88e5;
  flex-shrink: 0;
  margin-top: 2px;
  
  &:focus {
    outline: 2px solid #1e88e5;
    outline-offset: 2px;
  }
`;

export const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: #4a5568;
  line-height: 1.4;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    color: #2d3748;
  }
`;

export const CheckboxLink = styled.a`
  color: #1e88e5;
  text-decoration: none;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
    color: #1976d2;
  }
  
  &:focus {
    outline: 2px solid #1e88e5;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;